import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Save, Eye, TestTube, ArrowLeft } from "lucide-react";
import { BasicInfoTab } from "./workflow-editor/BasicInfoTab";
import { PhasesTab } from "./workflow-editor/PhasesTab";
import { DocumentsTab } from "./workflow-editor/DocumentsTab";
import { GuidanceTab } from "./workflow-editor/GuidanceTab";
import { SettingsTab } from "./workflow-editor/SettingsTab";
import { WorkflowPreviewModal } from "./WorkflowPreviewModal";
import { TestWorkflowModal } from "./workflow-editor/TestWorkflowModal";
import { ConditionalLogicTab } from "./workflow-editor/ConditionalLogicTab";
import { VersionHistoryTab } from "./workflow-editor/VersionHistoryTab";
import { DependenciesTab } from "./workflow-editor/DependenciesTab";
import { auditLog } from "@/lib/adminAuditLog";

interface WorkflowEditorProps {
  workflowId?: string;
}

export interface WorkflowData {
  id?: string;
  workflow_key: string;
  title: string;
  description: string;
  category: string;
  icon_name: string;
  estimated_time: string;
  difficulty: string;
  tier_restrictions: any;
  prerequisites: any;
  phases: any;
  success_criteria: any;
  guidance_notes: string;
  is_active: boolean;
  sort_order: number;
  version: number;
  change_notes: string;
  feature_flags: any;
}

export function WorkflowEditor({ workflowId }: WorkflowEditorProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!workflowId);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  
  const [workflowData, setWorkflowData] = useState<WorkflowData>({
    workflow_key: "",
    title: "",
    description: "",
    category: "member_operations",
    icon_name: "user-plus",
    estimated_time: "15-20 minutes",
    difficulty: "beginner",
    tier_restrictions: [],
    prerequisites: [],
    phases: [],
    success_criteria: [],
    guidance_notes: "",
    is_active: true,
    sort_order: 0,
    version: 1,
    change_notes: "",
    feature_flags: {},
  });

  useEffect(() => {
    if (workflowId) {
      loadWorkflow();
    }
  }, [workflowId]);

  async function loadWorkflow() {
    try {
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', workflowId)
        .single();

      if (error) throw error;

      setWorkflowData({
        ...data,
        tier_restrictions: Array.isArray(data.tier_restrictions) ? data.tier_restrictions : [],
        prerequisites: Array.isArray(data.prerequisites) ? data.prerequisites : [],
        phases: Array.isArray(data.phases) ? data.phases : [],
        success_criteria: Array.isArray(data.success_criteria) ? data.success_criteria : [],
        feature_flags: data.feature_flags || {},
      } as WorkflowData);
    } catch (error: any) {
      console.error('Error loading workflow:', error);
      toast({
        title: "Error",
        description: "Failed to load workflow",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    // Validation
    if (!workflowData.workflow_key || !workflowData.title || !workflowData.category) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (workflowData.phases.length === 0) {
      toast({
        title: "Validation Error",
        description: "Workflow must have at least one phase",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const workflowPayload = {
        ...workflowData,
        created_by: user?.id,
        updated_at: new Date().toISOString(),
      };

      if (workflowId) {
        // Update existing
        const { error } = await supabase
          .from('workflows')
          .update(workflowPayload)
          .eq('id', workflowId);

        if (error) throw error;

        await auditLog.workflowUpdated(workflowId, { action: 'updated', title: workflowData.title });

        toast({
          title: "Workflow Updated",
          description: "Changes saved successfully",
        });
      } else {
        // Create new
        const { data, error } = await supabase
          .from('workflows')
          .insert(workflowPayload)
          .select()
          .single();

        if (error) throw error;

        await auditLog.workflowUpdated(data.id, { action: 'created', title: workflowData.title });

        toast({
          title: "Workflow Created",
          description: "Workflow created successfully",
        });

        navigate(`/admin/templates/workflows/${data.id}/edit`);
      }
    } catch (error: any) {
      console.error('Error saving workflow:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save workflow",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-6">Loading workflow...</div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/templates/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {workflowId ? "Edit Workflow" : "Create Workflow"}
            </h1>
            {workflowData.title && (
              <p className="text-sm text-muted-foreground">{workflowData.title}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" onClick={() => setShowTestModal(true)} disabled={!workflowId}>
            <TestTube className="h-4 w-4 mr-2" />
            Test
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Workflow"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <TabsList className="w-full justify-start border-b rounded-none bg-background px-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="phases">Phases</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="conditional">Conditional Logic</TabsTrigger>
            <TabsTrigger value="guidance">Guidance</TabsTrigger>
            <TabsTrigger value="dependencies">Dependencies</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            {workflowId && <TabsTrigger value="versions">Versions</TabsTrigger>}
          </TabsList>

          <div className="p-6">
            <TabsContent value="basic" className="mt-0">
              <BasicInfoTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="phases" className="mt-0">
              <PhasesTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="documents" className="mt-0">
              <DocumentsTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="conditional" className="mt-0">
              <ConditionalLogicTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="guidance" className="mt-0">
              <GuidanceTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="dependencies" className="mt-0">
              <DependenciesTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <SettingsTab data={workflowData} onChange={setWorkflowData} />
            </TabsContent>

            {workflowId && (
              <TabsContent value="versions" className="mt-0">
                <VersionHistoryTab data={workflowData} onChange={setWorkflowData} />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>

      {/* Preview Modal */}
      <WorkflowPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        workflow={workflowData}
      />

      {/* Test Modal */}
      <TestWorkflowModal
        open={showTestModal}
        onClose={() => setShowTestModal(false)}
        workflow={workflowData}
      />
    </div>
  );
}